import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { IMAGES } from "../lib/api";

const links = [
  { to: "/", label: "Home", id: "home" },
  { to: "/shop", label: "Shop", id: "shop" },
  { to: "/custom-order", label: "Custom Order", id: "custom" },
  { to: "/#about", label: "About", id: "about" },
];

export const Logo = ({ size = "h-14 w-14" }) => (
  <span className={`sticker inline-flex ${size} items-center justify-center overflow-hidden bg-white p-0.5`}>
    <img src={IMAGES.logo} alt="L3's Kitchen Co. and More logo" className="h-full w-full rounded-full object-contain" />
  </span>
);

export default function Header() {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const navCls = (l) => () => {
    const active = l.to.includes("#") ? `${pathname}${hash}` === l.to : pathname === l.to && !(l.to === "/" && hash);
    return `rounded-full px-4 py-2 text-sm font-extrabold transition-colors ${active ? "bg-white text-black" : "text-white/80 hover:text-[var(--pink)]"}`;
  };

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[var(--ink)]/85 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3" data-testid="header-logo-link">
          <Logo size="h-12 w-12 sm:h-14 sm:w-14" />
          <span className="min-w-0 leading-none">
            <span className="font-display block truncate text-lg sm:text-xl"><span className="text-[var(--pink)]">L3's</span> Kitchen Co.</span>
            <span className="font-script text-xs sm:text-sm text-[var(--blue-soft)]">and More Sweet Treats</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <NavLink key={l.id} to={l.to} end className={navCls(l)} data-testid={`nav-${l.id}-link`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/cart" className="btn-candy relative !px-4 !py-2.5" data-testid="header-cart-button">
            <ShoppingBag className="h-5 w-5" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-black bg-[var(--blue)] px-1 text-xs" data-testid="header-cart-count">{count}</span>
            )}
          </Link>
          <button className="rounded-full p-3 md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu" aria-expanded={open}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="flex flex-col gap-1 border-t border-white/10 px-4 py-3 md:hidden" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink key={l.id} to={l.to} end className={(a) => `${navCls(l)(a)} !py-3 !text-base`} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}-link`}>{l.label}</NavLink>
          ))}
        </nav>
      )}
      <div className="ribbon-thin" />
    </header>
  );
}
